import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import {
  Loader2, Search, Filter, Download, X, ShieldCheck, ArrowRight, ChevronLeft, ChevronRight, Monitor,
} from 'lucide-react';

const MODULE_CLS = {
  Sellers: 'bg-indigo-100 text-indigo-700',
  Buyers: 'bg-sky-100 text-sky-700',
  'Guest Sellers': 'bg-violet-100 text-violet-700',
  Admin: 'bg-amber-100 text-amber-700',
  Payments: 'bg-emerald-100 text-emerald-700',
  Orders: 'bg-orange-100 text-orange-700',
  Other: 'bg-neutral-200 text-neutral-600',
};
const CAT_CLS = {
  created: 'bg-emerald-100 text-emerald-700',
  updated: 'bg-sky-100 text-sky-700',
  activated: 'bg-emerald-100 text-emerald-700',
  deactivated: 'bg-rose-100 text-rose-600',
  deleted: 'bg-rose-100 text-rose-600',
  viewed: 'bg-violet-100 text-violet-700',
  login: 'bg-amber-100 text-amber-700',
  other: 'bg-neutral-200 text-neutral-600',
};

function fmtWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const DATE_PRESETS = [
  { v: '', label: 'All time' },
  { v: 'today', label: 'Today' },
  { v: 'yesterday', label: 'Yesterday' },
  { v: '7d', label: 'Last 7 days' },
  { v: '30d', label: 'Last 30 days' },
  { v: 'custom', label: 'Custom range' },
];
const CATEGORIES = ['created', 'updated', 'activated', 'deactivated', 'deleted', 'viewed', 'login', 'other'];

export default function AdminAuditLogs() {
  const [data, setData] = useState({ items: [], total: 0, admins: [], modules: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ date_preset: '', module: '', category: '', admin: '', date_from: '', date_to: '' });
  const [selected, setSelected] = useState(null);

  useEffect(() => { const t = setTimeout(() => setDq(q), 350); return () => clearTimeout(t); }, [q]);

  const params = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (dq) p.q = dq;
    if (filters.module) p.module = filters.module;
    if (filters.category) p.category = filters.category;
    if (filters.admin) p.admin = filters.admin;
    if (filters.date_preset && filters.date_preset !== 'custom') p.date_preset = filters.date_preset;
    if (filters.date_preset === 'custom') {
      if (filters.date_from) p.date_from = filters.date_from;
      if (filters.date_to) p.date_to = filters.date_to;
    }
    return p;
  }, [page, pageSize, dq, filters]);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/admin/audit-logs', { params }).then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, [params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [dq, filters, pageSize]);

  const activeFilterCount = ['date_preset', 'module', 'category', 'admin'].filter((k) => filters[k]).length;

  function exportCsv() {
    const usp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (k !== 'page' && k !== 'page_size') usp.append(k, v); });
    usp.append('format', 'csv');
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const token = localStorage.getItem('fab_token');
    fetch(`${base}/api/admin/audit-logs?${usp.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement('a');
        a.href = url; a.download = 'audit-logs.csv'; a.click(); URL.revokeObjectURL(url);
      });
  }

  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  return (
    <div data-testid="admin-audit-logs">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-fab-primary-lighter grid place-items-center text-fab-primary shrink-0">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold text-fab-text" data-testid="audit-title">Audit Logs</h2>
          <p className="text-sm text-fab-text-secondary mt-0.5">Track every important administrative action and change across the FABTEXS AI platform.</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="mt-4 bg-white border border-fab-primary-lighter rounded-xl p-3 flex items-center gap-2.5 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-fab-text-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="audit-search"
            placeholder="Search admin, seller, buyer, action, module, IP..."
            className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-fab-primary-lighter bg-fab-base/40 focus:outline-none focus:ring-2 focus:ring-fab-primary/30" />
        </div>
        <div className="relative">
          <button onClick={() => setShowFilters((s) => !s)} data-testid="audit-filters-btn"
            className="flex items-center gap-2 px-4 py-2.5 text-sm rounded-lg border border-fab-primary-lighter text-fab-text-secondary hover:border-fab-primary hover:text-fab-primary transition">
            <Filter className="w-4 h-4" /> Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
          </button>
          {showFilters && (
            <div className="absolute right-0 mt-2 w-72 bg-white border border-fab-primary-lighter rounded-xl shadow-xl p-3 z-30 space-y-3" data-testid="audit-filter-panel">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Date</label>
                <select value={filters.date_preset} onChange={(e) => setFilters({ ...filters, date_preset: e.target.value })} data-testid="audit-filter-date"
                  className="w-full text-sm rounded-lg border border-fab-primary-lighter px-2 py-2 bg-white">
                  {DATE_PRESETS.map((d) => <option key={d.v} value={d.v}>{d.label}</option>)}
                </select>
                {filters.date_preset === 'custom' && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="audit-filter-from" className="text-[12px] rounded-lg border border-fab-primary-lighter px-2 py-1.5" />
                    <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="audit-filter-to" className="text-[12px] rounded-lg border border-fab-primary-lighter px-2 py-1.5" />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Module</label>
                <select value={filters.module} onChange={(e) => setFilters({ ...filters, module: e.target.value })} data-testid="audit-filter-module"
                  className="w-full text-sm rounded-lg border border-fab-primary-lighter px-2 py-2 bg-white">
                  <option value="">All modules</option>
                  {(data.modules || []).map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Action</label>
                <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} data-testid="audit-filter-category"
                  className="w-full text-sm rounded-lg border border-fab-primary-lighter px-2 py-2 bg-white capitalize">
                  <option value="">All actions</option>
                  {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-1">Admin / User</label>
                <select value={filters.admin} onChange={(e) => setFilters({ ...filters, admin: e.target.value })} data-testid="audit-filter-admin"
                  className="w-full text-sm rounded-lg border border-fab-primary-lighter px-2 py-2 bg-white">
                  <option value="">All users</option>
                  {(data.admins || []).map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div className="flex justify-between pt-1">
                <button onClick={() => setFilters({ date_preset: '', module: '', category: '', admin: '', date_from: '', date_to: '' })} data-testid="audit-filter-clear" className="text-[12.5px] text-fab-text-muted hover:text-fab-text">Clear</button>
                <button onClick={() => setShowFilters(false)} data-testid="audit-filter-apply" className="text-[12.5px] px-3 py-1.5 rounded-lg bg-fab-primary text-white">Apply</button>
              </div>
            </div>
          )}
        </div>
        <button onClick={exportCsv} data-testid="audit-export-btn"
          className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-lg border border-fab-primary-lighter text-fab-text-secondary hover:border-fab-primary hover:text-fab-primary transition">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="mt-4 bg-white border border-fab-primary-lighter rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-fab-text-muted border-b border-fab-primary-lighter">
                {['Date & Time', 'Admin / User', 'Action', 'Module', 'Target', 'Old Value', 'New Value', 'IP / Device'].map((h) => (
                  <th key={h} className="px-3 py-3 font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-fab-text-muted"><Loader2 className="inline w-5 h-5 animate-spin mr-2" /> Loading…</td></tr>
              ) : data.items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-fab-text-muted" data-testid="audit-empty">No audit records match your filters.</td></tr>
              ) : data.items.map((r) => (
                <tr key={r.id} onClick={() => setSelected(r)} data-testid={`audit-row-${r.id}`}
                  className="border-b border-fab-primary-lighter/60 hover:bg-fab-base/50 cursor-pointer">
                  <td className="px-3 py-3 whitespace-nowrap text-fab-text-secondary">{fmtWhen(r.timestamp)}</td>
                  <td className="px-3 py-3 whitespace-nowrap font-medium text-fab-text">{r.actor}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold capitalize ${CAT_CLS[r.category] || CAT_CLS.other}`}>{r.category}</span>
                      <span className="text-fab-text">{r.action_label}</span>
                    </span>
                  </td>
                  <td className="px-3 py-3"><span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${MODULE_CLS[r.module] || MODULE_CLS.Other}`}>{r.module}</span></td>
                  <td className="px-3 py-3 font-medium text-fab-text max-w-[180px] truncate" title={r.target}>{r.target}</td>
                  <td className="px-3 py-3 text-rose-600 max-w-[160px] truncate" title={r.old_value ?? ''}>{r.old_value == null ? '—' : String(r.old_value)}</td>
                  <td className="px-3 py-3 text-emerald-700 max-w-[160px] truncate" title={r.new_value ?? ''}>{r.new_value == null ? '—' : String(r.new_value)}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-[11.5px] text-fab-text-muted">
                    <div className="font-mono">{r.ip}</div>
                    <div>{r.device}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {!loading && data.total > 0 && (
          <div className="px-4 py-3 border-t border-fab-primary-lighter flex items-center justify-between flex-wrap gap-2">
            <div className="text-[12.5px] text-fab-text-secondary" data-testid="audit-count">
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, data.total)} of {data.total} records
            </div>
            <div className="flex items-center gap-2">
              <select value={pageSize} onChange={(e) => setPageSize(+e.target.value)} data-testid="audit-page-size" className="text-[12.5px] rounded-lg border border-fab-primary-lighter px-2 py-1.5 bg-white">
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="audit-prev" className="p-1.5 rounded-lg border border-fab-primary-lighter disabled:opacity-40 hover:border-fab-primary"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-[12.5px] text-fab-text-secondary">{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="audit-next" className="p-1.5 rounded-lg border border-fab-primary-lighter disabled:opacity-40 hover:border-fab-primary"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="audit-detail-drawer">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-white h-full shadow-2xl overflow-y-auto">
            <div className="p-5 border-b border-fab-primary-lighter flex items-start justify-between sticky top-0 bg-white">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold capitalize ${CAT_CLS[selected.category] || CAT_CLS.other}`}>{selected.category}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10.5px] font-semibold ${MODULE_CLS[selected.module] || MODULE_CLS.Other}`}>{selected.module}</span>
                </div>
                <h3 className="text-lg font-bold text-fab-text mt-2">{selected.action_label}</h3>
              </div>
              <button onClick={() => setSelected(null)} data-testid="audit-detail-close" className="text-fab-text-muted hover:text-fab-text"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4 text-[13px]">
              <dl className="grid grid-cols-3 gap-y-2.5">
                <dt className="text-fab-text-muted">Admin</dt><dd className="col-span-2 font-medium text-fab-text">{selected.actor}</dd>
                <dt className="text-fab-text-muted">Date</dt><dd className="col-span-2 text-fab-text">{fmtWhen(selected.timestamp)}</dd>
                <dt className="text-fab-text-muted">Module</dt><dd className="col-span-2 text-fab-text">{selected.module}</dd>
                <dt className="text-fab-text-muted">Target</dt><dd className="col-span-2 text-fab-text">{selected.target}</dd>
                {selected.reason && (<><dt className="text-fab-text-muted">Reason</dt><dd className="col-span-2 text-fab-text">{selected.reason}</dd></>)}
              </dl>

              <div>
                <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-2">Change</div>
                {selected.changes && selected.changes.length > 0 ? (
                  <table className="w-full text-[12.5px] border border-fab-primary-lighter rounded-lg overflow-hidden">
                    <thead><tr className="bg-fab-base/50 text-[11px] uppercase text-fab-text-muted">
                      <th className="text-left px-2.5 py-1.5">Field</th><th className="text-left px-2.5 py-1.5">Before</th><th className="text-left px-2.5 py-1.5">After</th>
                    </tr></thead>
                    <tbody>
                      {selected.changes.map((c, i) => (
                        <tr key={i} className="border-t border-fab-primary-lighter/60">
                          <td className="px-2.5 py-1.5 font-medium">{c.field}</td>
                          <td className="px-2.5 py-1.5 text-rose-600">{c.before == null ? '—' : String(c.before)}</td>
                          <td className="px-2.5 py-1.5 text-emerald-700">{c.after == null ? '—' : String(c.after)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="flex items-center gap-2 text-[12.5px]">
                    <span className="text-rose-600">{selected.old_value == null ? '—' : String(selected.old_value)}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-fab-text-muted" />
                    <span className="text-emerald-700">{selected.new_value == null ? '—' : String(selected.new_value)}</span>
                  </div>
                )}
              </div>

              <div>
                <div className="text-[11px] uppercase tracking-wider text-fab-text-muted font-semibold mb-2 flex items-center gap-1.5"><Monitor className="w-3.5 h-3.5" /> Request Information</div>
                <dl className="grid grid-cols-3 gap-y-2.5">
                  <dt className="text-fab-text-muted">IP Address</dt><dd className="col-span-2 font-mono text-fab-text">{selected.ip}</dd>
                  <dt className="text-fab-text-muted">Device</dt><dd className="col-span-2 text-fab-text">{selected.device}</dd>
                  <dt className="text-fab-text-muted">User Agent</dt><dd className="col-span-2 text-[11px] text-fab-text-muted break-all">{selected.user_agent}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
